// TODO: Add pagination (see shadcn/List.tsx for reference)
import React, { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { List as Interface, Component, Switch as SwitchProps, Icon as IconProps, Code as CodeProps, Text as TextProps, Color as ColorProps, Pdf as PdfProps } from '../../design/components';
import type { Property } from '../../design/types';
import { Text } from './Text';
import { Switch } from './Switch';
import { Icon } from './Icon';
import { Code } from './Code';
import { Color } from './Color';
import { Pdf } from './Pdf';
import { parseGeopoint, initLeafletIcon, calculateBounds, DEFAULT_CENTER, DEFAULT_ZOOM } from '../shared/geopoint';
import { isFileMetadata } from '../shared/image';

initLeafletIcon();

/**
 * Render a cell value using the component (inline mode).
 * All component types render with inline={true} and readonly={true}.
 */
const renderCellValue = (
  property: Property,
  component?: Component
): React.ReactNode => {
  if (!component) return '';

  const value = component.state?.[0];
  if (value === null || value === undefined) return '';

  // Render component inline based on property type
  switch (property.type) {
    case 'boolean':
      return <Switch {...component as SwitchProps} inline readonly />;
    case 'icon':
      return <Icon {...component as IconProps} inline readonly />;
    case 'code':
      return <Code {...component as CodeProps} inline readonly />;
    case 'color':
      return <Color {...component as ColorProps} inline readonly />;
    case 'pdf':
      return <Pdf {...component as PdfProps} inline readonly />;
    case 'reference':
    case 'object':
      return <List {...component as Interface} properties={property.reference?.properties || property.properties || []} inline readonly />;
    case 'custom':
      return null;
    default:
      return <Text {...component as TextProps} inline readonly />;
  }
};

interface MarkerData {
  rowIndex: number;
  coords: [number, number];
  components: Component[];
}

function FitBounds({ markers }: { markers: MarkerData[] }) {
  const map = useMap();

  React.useEffect(() => {
    if (markers.length === 0) return;

    const bounds = calculateBounds(markers.map(m => m.coords));
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [markers, map]);

  return null;
}

type ViewMode = 'table' | 'map' | 'card';

/**
 * Get a unique identifier for a row of components.
 * Uses the 'id' component's state value if available.
 */
const getRowId = (rowComponents: Component[]): string => {
  const idComponent = rowComponents.find(c => c.name === 'id');
  return String(idComponent?.state?.[0] ?? '');
};

export const List = ({
  name,
  properties,
  state,
  hidden,
  inline,
  label,
  disabled,
  readonly,
  onRowClick,
  options,
  limits,
  action,
  view,
}: Interface): React.ReactElement | null => {
  const [rawSelected, setSelectedRows] = state || [[], () => {}];
  if (rawSelected != null && !Array.isArray(rawSelected)) {
    console.warn('List: state[0] is not an array, got', typeof rawSelected, rawSelected);
  }
  const selectedRows = Array.isArray(rawSelected) ? rawSelected : [];
  const [limitError, setLimitError] = useState<string | null>(null);

  // Determine what items to display and mode
  const rows = options || selectedRows;
  const isEditMode = !!options && !readonly;

  // Selection is tracked by row IDs for efficient lookup
  const selectedIds = useMemo(
    () => new Set(selectedRows.map(row => getRowId(row))),
    [selectedRows]
  );

  const [min, max] = limits || [0, Infinity];

  const toggleItem = (rowIndex: number) => {
    const rowComponents = rows[rowIndex];
    const rowId = getRowId(rowComponents);
    setLimitError(null);

    // For max=1 (single select), set row directly instead of array
    const setValue = setSelectedRows as unknown as (v: Component[] | Component[][] | null) => void;
    if (selectedIds.has(rowId)) {
      // Deselect: for max=1, set null; otherwise filter array
      if (max === 1) {
        setValue(null);
      } else {
        setSelectedRows(selectedRows.filter(r => getRowId(r) !== rowId));
      }
    } else {
      // Select: for max=1, set single row; otherwise append to array
      if (max === 1) {
        setValue(rowComponents);
        // Auto-close when max reached
        if (action) {
          action[1]();
        }
      } else {
        const newSelection = [...selectedRows, rowComponents];
        setSelectedRows(newSelection);
        // Auto-close when max reached
        if (newSelection.length >= max && action) {
          action[1]();
        }
      }
    }
  };

  const visibleProperties = properties.filter(p => !p.hidden);
  const [viewState, setViewState] = useState<{ mode: ViewMode; modelName: string | undefined }>({
    mode: view || 'table',
    modelName: name,
  });

  // Reset view mode when model changes
  const viewMode = viewState.modelName === name ? viewState.mode : (view || 'table');
  const setViewMode = (mode: ViewMode) => setViewState({ mode, modelName: name });

  const geopointProp = useMemo(
    () => properties.find(p => p.type === 'geopoint'),
    [properties]
  );

  const imageProp = useMemo(
    () => properties.find(p => p.type === 'image'),
    [properties]
  );

  const hasAlternateViews = geopointProp || imageProp;

  const markersData = useMemo(() => {
    if (!geopointProp) return [];
    return rows
      .map((rowComponents, rowIndex) => {
        const geopointComponent = rowComponents.find(c => c.name === geopointProp.name);
        const value = geopointComponent?.state?.[0] as string | undefined;
        const coords = parseGeopoint(value);
        return coords ? { rowIndex, coords, components: rowComponents } : null;
      })
      .filter((m): m is MarkerData => m !== null);
  }, [rows, geopointProp]);

  // Inline mode: just render the label
  if (inline) {
    return <span>{label || ''}</span>;
  }

  const handleRowClick = (rowIndex: number) => {
    if (disabled) return;
    if (isEditMode) {
      toggleItem(rowIndex);
    } else {
      onRowClick?.(rowIndex);
    }
  };

  const styles = {
    container: {
      display: hidden ? 'none' : undefined,
      opacity: disabled ? 0.6 : 1,
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '4px',
      padding: '8px',
      borderBottom: '1px solid #ddd',
    },
    toggleButton: {
      background: 'none',
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '6px 10px',
      cursor: 'pointer',
      fontSize: '14px',
    },
    toggleButtonActive: {
      background: '#1976d2',
      border: '1px solid #1976d2',
      borderRadius: '4px',
      padding: '6px 10px',
      cursor: 'pointer',
      fontSize: '14px',
      color: 'white',
    },
    actionButton: {
      background: 'none',
      border: '1px solid #1976d2',
      borderRadius: '4px',
      padding: '4px 12px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#1976d2',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
    },
    th: {
      textAlign: 'left' as const,
      padding: '12px 16px',
      borderBottom: '2px solid #ddd',
      backgroundColor: '#f5f5f5',
      fontWeight: 600,
    },
    td: {
      padding: '12px 16px',
      borderBottom: '1px solid #eee',
    },
    tr: {
      cursor: disabled ? 'default' : 'pointer',
    },
    trSelected: {
      cursor: disabled ? 'default' : 'pointer',
      backgroundColor: '#e3f2fd',
    },
    checkboxCell: {
      padding: '12px 8px 12px 16px',
      borderBottom: '1px solid #eee',
      width: '48px',
    },
    checkboxHeader: {
      padding: '12px 8px 12px 16px',
      borderBottom: '2px solid #ddd',
      backgroundColor: '#f5f5f5',
      width: '48px',
    },
    empty: {
      textAlign: 'center' as const,
      padding: '24px',
      color: '#666',
    },
    mapContainer: {
      height: 400,
      width: '100%',
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '16px',
      padding: '16px',
    },
    card: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: disabled ? 'default' : 'pointer',
      transition: 'box-shadow 0.2s',
    },
    cardImage: {
      width: '100%',
      height: '160px',
      objectFit: 'cover' as const,
      display: 'block',
    },
    cardContent: {
      padding: '12px',
    },
    cardTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
    cardText: {
      margin: '4px 0 0 0',
      fontSize: '14px',
      color: '#666',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
  };

  const renderTable = () => (
    <table style={styles.table} aria-label={name}>
      <thead>
        <tr>
          {isEditMode && <th style={styles.checkboxHeader} />}
          {visibleProperties.map(property => (
            <th key={property.name} style={styles.th}>
              {property.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={visibleProperties.length + (isEditMode ? 1 : 0)}
              style={{ ...styles.td, ...styles.empty }}
            >
              No data
            </td>
          </tr>
        ) : (
          rows.map((rowComponents, rowIndex) => {
            const rowId = getRowId(rowComponents);
            const isSelected = selectedIds.has(rowId);
            return (
              <tr
                key={rowId || rowIndex}
                style={isEditMode && isSelected ? styles.trSelected : styles.tr}
                onClick={() => handleRowClick(rowIndex)}
                onMouseEnter={e => {
                  if (!disabled && !(isEditMode && isSelected)) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={e => {
                  if (isEditMode && isSelected) {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                  } else {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {isEditMode && (
                  <td style={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                )}
                {visibleProperties.map(property => {
                  const component = rowComponents.find((c: Component) => c.name === property.name);
                  return (
                    <td key={property.name} style={styles.td}>
                      {renderCellValue(property, component)}
                    </td>
                  );
                })}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  const renderMap = () => {
    // Properties to show in popup (exclude image and geopoint)
    const popupProperties = visibleProperties.filter(
      p => p.type !== 'image' && p.type !== 'geopoint'
    );

    return (
      <div style={styles.mapContainer}>
        <MapContainer
          center={markersData.length > 0 ? markersData[0].coords : DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {markersData.map(marker => {
            const rowId = getRowId(marker.components);
            const isSelected = selectedIds.has(rowId);
            return (
              <Marker
                key={rowId || marker.rowIndex}
                position={marker.coords}
                opacity={isEditMode && !isSelected ? 0.5 : 1}
              >
                <Popup>
                  <div style={{ minWidth: 150 }}>
                    {isEditMode && (
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItem(marker.rowIndex)}
                          style={{ cursor: 'pointer', marginRight: '8px' }}
                        />
                        <span style={{ fontSize: '13px', color: '#666' }}>
                          {isSelected ? 'Selected' : 'Not selected'}
                        </span>
                      </div>
                    )}
                    {popupProperties.slice(0, 4).map((property, index) => {
                      const component = marker.components.find((c: Component) => c.name === property.name);
                      const value = renderCellValue(property, component);
                      return (
                        <p
                          key={property.name}
                          style={{
                            margin: index === 0 ? 0 : '4px 0 0 0',
                            fontWeight: index === 0 ? 600 : 400,
                            fontSize: index === 0 ? '14px' : '13px',
                            color: index === 0 ? '#333' : '#666',
                          }}
                        >
                          {value}
                        </p>
                      );
                    })}
                    <a
                      onClick={() => isEditMode ? toggleItem(marker.rowIndex) : onRowClick?.(marker.rowIndex)}
                      style={{
                        display: 'block',
                        marginTop: '8px',
                        color: '#1976d2',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                    >
                      {isEditMode ? (isSelected ? 'Deselect' : 'Select') : 'View details'}
                    </a>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          <FitBounds markers={markersData} />
        </MapContainer>
      </div>
    );
  };

  const renderCards = () => {
    // Properties to show in card (exclude image and geopoint)
    const cardProperties = visibleProperties.filter(
      p => p.type !== 'image' && p.type !== 'geopoint'
    );

    if (rows.length === 0) {
      return (
        <div style={styles.empty}>No data</div>
      );
    }

    return (
      <div style={styles.cardGrid}>
        {rows.map((rowComponents, rowIndex) => {
          const rowId = getRowId(rowComponents);
          const imageComponent = imageProp
            ? rowComponents.find(c => c.name === imageProp.name)
            : undefined;
          const rawImage = imageComponent?.state?.[0];
          const imageUrl = isFileMetadata(rawImage) ? rawImage.url : undefined;
          const isSelected = selectedIds.has(rowId);

          return (
            <div
              key={rowId || rowIndex}
              style={{
                ...styles.card,
                position: 'relative' as const,
                ...(isEditMode && isSelected && {
                  outline: '2px solid #1976d2',
                }),
              }}
              onClick={() => handleRowClick(rowIndex)}
              onMouseEnter={e => {
                if (!disabled) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {isEditMode && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    zIndex: 1,
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px',
                  }}
                />
              )}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt=""
                  style={styles.cardImage}
                />
              )}
              <div style={styles.cardContent}>
                {cardProperties.slice(0, 4).map((property, index) => {
                  const component = rowComponents.find((c: Component) => c.name === property.name);
                  const value = renderCellValue(property, component);
                  return (
                    <p
                      key={property.name}
                      style={index === 0 ? styles.cardTitle : styles.cardText}
                    >
                      {value}
                    </p>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'map':
        return renderMap();
      case 'card':
        return renderCards();
      default:
        return renderTable();
    }
  };

  const hasToolbar = hasAlternateViews || action || limitError;

  // Handle action click with limit validation
  const handleActionClick = () => {
    if (!action) return;
    // Check min limit when closing selection (action label is "Done")
    if (isEditMode && min > 0 && selectedRows.length < min) {
      setLimitError(`Select at least ${min} item${min > 1 ? 's' : ''}`);
      return;
    }
    setLimitError(null);
    action[1]();
  };

  return (
    <div style={styles.container}>
      {hasToolbar && (
        <div style={{ ...styles.toolbar, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {action && (
              <button
                style={styles.actionButton}
                onClick={handleActionClick}
              >
                {action[0]}
              </button>
            )}
            {limitError && (
              <span style={{ color: '#d32f2f', fontSize: '14px' }}>{limitError}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {hasAlternateViews && (
              <>
                <button
                  style={viewMode === 'table' ? styles.toggleButtonActive : styles.toggleButton}
                  onClick={() => setViewMode('table')}
                  title="Table view"
                >
                  {'\u{1F4CB}'}
                </button>
                {imageProp && (
                  <button
                    style={viewMode === 'card' ? styles.toggleButtonActive : styles.toggleButton}
                    onClick={() => setViewMode('card')}
                    title="Card view"
                  >
                    {'\u{1F5BC}'}
                  </button>
                )}
                {geopointProp && (
                  <button
                    style={viewMode === 'map' ? styles.toggleButtonActive : styles.toggleButton}
                    onClick={() => setViewMode('map')}
                    title="Map view"
                  >
                    {'\u{1F5FA}'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {renderContent()}
    </div>
  );
};
